import React, { useState, useRef, useEffect } from 'react';

// Sentinel value meaning "unattached / no club" (stored as externalClubName: null on save).
export const UNATTACHED = '__unattached__';

// A searchable, creatable club picker for meet registration. The admin can't predefine the club list (we don't know
// which clubs will turn up), so it's seeded from the clubs already entered for this meet and grows as new ones are
// added. Filtering nudges admins to pick an existing club rather than retype a variant; "+ Add" only shows when the
// typed text isn't already an option. Value convention: '' = nothing chosen yet, UNATTACHED = no club, else a club name.
const CreatableClubSelect = ({
  clubs = [],
  value = '',
  onChange,
  disabled = false,
  unattachedLabel = 'Unattached / no club',
  placeholder = 'Select a club…',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const display = value === UNATTACHED ? unattachedLabel : value || '';

  const q = query.trim();
  const qLower = q.toLowerCase();
  const filtered = clubs.filter((c) => c.toLowerCase().includes(qLower));
  const exactExists =
    clubs.some((c) => c.toLowerCase() === qLower) ||
    qLower === unattachedLabel.toLowerCase();
  const showAdd = q.length > 0 && !exactExists;

  const choose = (v) => {
    onChange(v);
    setOpen(false);
    setQuery('');
  };

  return (
    <div style={styles.wrap} ref={ref}>
      <button
        type="button"
        style={{ ...styles.button, ...(disabled ? styles.buttonDisabled : {}) }}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        <span style={display ? styles.value : styles.placeholder}>
          {display || placeholder}
        </span>
        {!disabled && <span style={styles.caret}>▾</span>}
      </button>
      {open && !disabled && (
        <div style={styles.panel}>
          <input
            style={styles.search}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search or add a club…"
            autoFocus
          />
          <div style={styles.list}>
            <button
              type="button"
              style={styles.option}
              onClick={() => choose(UNATTACHED)}
            >
              {unattachedLabel}
            </button>
            <div style={styles.divider} />
            {filtered.map((c) => (
              <button
                key={c}
                type="button"
                style={styles.option}
                onClick={() => choose(c)}
              >
                {c}
              </button>
            ))}
            {filtered.length === 0 && !showAdd && (
              <div style={styles.empty}>No matching clubs</div>
            )}
            {showAdd && (
              <button
                type="button"
                style={styles.addOption}
                onClick={() => choose(q)}
              >
                + Add “{q}”
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  wrap: { position: 'relative' },
  button: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 4,
    fontSize: 14,
    background: '#fff',
    cursor: 'pointer',
    textAlign: 'left',
  },
  buttonDisabled: {
    background: '#f3f4f6',
    color: '#6b7280',
    cursor: 'not-allowed',
  },
  value: {
    color: '#111827',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  placeholder: { color: '#9ca3af' },
  caret: { color: '#6b7280', fontSize: 12, flexShrink: 0 },
  panel: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    zIndex: 20,
    background: '#fff',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
    overflow: 'hidden',
  },
  search: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 10px',
    border: 'none',
    borderBottom: '1px solid #e5e7eb',
    fontSize: 14,
    outline: 'none',
  },
  list: {
    maxHeight: 200,
    overflowY: 'auto',
    padding: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  option: {
    textAlign: 'left',
    padding: '8px 10px',
    border: 'none',
    background: 'none',
    fontSize: 14,
    color: '#111827',
    cursor: 'pointer',
    borderRadius: 4,
  },
  addOption: {
    textAlign: 'left',
    padding: '8px 10px',
    border: 'none',
    background: 'none',
    fontSize: 14,
    color: '#2563eb',
    fontWeight: 600,
    cursor: 'pointer',
    borderRadius: 4,
  },
  divider: { height: 1, background: '#e5e7eb', margin: '4px 0' },
  empty: { padding: '8px 10px', color: '#9ca3af', fontSize: 13 },
};

export default CreatableClubSelect;
